import { reactive, computed, toRefs } from 'vue'
import { getFileUrl } from '@/filters'
import { fetchSystemParams } from '@/services/api/common'
import { queryLoginData } from '@/services/api/account'
import { useLoginStore } from './login'
import { defineStore } from '../store'
import eventBus from '@/services/bus'
import { ETradeMode } from '@/constants/client'

/**
 * 用户存储对象
 * @returns 
 */
export const useUserStore = defineStore(() => {
    const loginStore = useLoginStore()

    const state = reactive({
        loading: false,
        userData: <Model.LoginQueryRsp>Object.create({
            arearole: [],
            exchangeRateConfigs: [],
            externalExchanges: [],
            goodsgroups: [],
            loginAccount: {},
            markets: [],
            userAccount: {},
            userInfo: {},
            username: '',
        }),
        systemParams: <Model.SystemParam[]>[]
    })

    // 用户信息
    const userInfo = computed(() => state.userData.userInfo)

    // 用户信息
    const userAccount = computed(() => state.userData.userAccount)

    // 登录机构名称
    const accountName = computed(() => {
        const { userAccount } = state.userData
        return userAccount?.accountname
    })

    // 客户名称
    const customerName = computed(() => {
        const { userInfo } = state.userData
        return userInfo?.customername
    })

    // 账户类型
    const userType = computed(() => {
        const { userAccount } = state.userData
        return userAccount?.usertype
    })

    // 用户头像
    const userAvatar = computed(() => {
        const file = userInfo.value?.headurl
        return file ? getFileUrl(file) : ''
    })

    // 是否已实名认证
    const hasAuth = computed(() => {
        const { userAccount } = state.userData
        return userAccount?.hasauth === 1
    })

    // 所属会员ID
    const memberUserId = computed(() => {
        const { userAccount } = state.userData
        return userAccount?.memberuserid ?? 0
    })

    // 获取系统参数
    const getSystemParams = async () => {
        try {
            if (!state.systemParams.length) {
                const res = await fetchSystemParams()
                state.systemParams = res.data
            }
        } catch (err) {
            console.error(err)
        }
    }

    // 获取用户数据
    const getUserData = async () => {
        try {
            state.loading = true
            const res = await queryLoginData({
                data: {
                    loginID: loginStore.loginId
                }
            })
            state.userData = res.data
        } finally {
            state.loading = false
        }
    }

    // 获取市场ID
    const getMarketId = (tradeMode: keyof typeof ETradeMode) => {
        const { marketid } = state.userData.markets.find(obj => obj.trademode === ETradeMode[tradeMode]) ?? {}
        return marketid ?? 0
    }

    // 获取市场名称
    const getMarketName = (marketid: number) => {
        const { marketname } = state.userData.markets.find(obj => obj.marketid === marketid) ?? {}
        return marketname ?? ''
    }

    // 是否有某个市场权限
    const hasMarket = (tradeMode: keyof typeof ETradeMode) => {
        return state.userData.markets.some((e) => e.trademode === ETradeMode[tradeMode])
    }

    // 获取用户数据
    const getUserDataInfo = <K extends keyof Model.LoginQueryRsp>(key: K) => {
        return state.userData[key]!
    }

    // 获取对应系统参数的对应值
    const getSystemParamValue = (paramcode: string) => {
        const { systemParams = state.systemParams } = state.userData
        return systemParams.find(obj => {
            return obj.paramcode === paramcode
        })?.paramvalue
    }

    // 清除用户信息，待优化
    const clearUserData = () => {
        state.userData = <Model.LoginQueryRsp>Object.create({
            arearole: [],
            exchangeRateConfigs: [],
            externalExchanges: [],
            goodsgroups: [],
            loginAccount: {},
            markets: [],
            userAccount: {},
            userInfo: {},
            username: '',
        })
    }

    // 接收账户变更通知
    const userChangeNtf = eventBus.$on('UserChangeNtf', () => getUserData())

    return {
        ...toRefs(state),
        userInfo,
        userAccount,
        accountName,
        customerName,
        userType,
        userAvatar,
        hasAuth,
        memberUserId,
        getUserData,
        getUserDataInfo,
        getSystemParams,
        getSystemParamValue,
        userChangeNtf,
        getMarketId,
        hasMarket,
        getMarketName,
        clearUserData
    }
})